import URLX from './Url';
import { Action } from 'redux';
import * as itemCache from './CurrentPage/cache';
import * as appShell from './AppShell/utils';
import { clearCacheAndUnregisterServiceWorker } from './service-worker/registration';
import { isIE } from './device-type';

export const LINK_IDENTIFIER_HEADER = 'X-Link-Identifier';
export const REQUEST_ACTION_HEADER = 'X-Request-Action';
export const INCLUDE_APP_SHELL_DATA_HEADER = 'X-IncludeAppShellData';
export const VERSION_HEADER_NAME = 'X-Server-Version';

export const ADD_TO_CART_ACTION = 'addtocart';
export const CLICK_ACTION = 'click';

export type LoadFailureAction = Action & {
  error: any;
  url: URLX;
};

type LoadFailure = {
  status: number;
};

export function get(url: string | URLX, headers?: { [name: string]: string }) {
  return load(url, 'get', undefined, headers);
}

export function getItem<T>(url: string | URLX, useCache: boolean = false) {
  url = typeof url === 'string' ? new URLX(url) : url;
  let cachePromise: Promise<T> | null = null;
  if (useCache) {
    const cacheItem = itemCache.getCacheItem(url);
    if (cacheItem) {
      cachePromise = Promise.resolve((cacheItem as any) as T);
    }
  }

  // Even if we use the cache we still want to get a fresh version of it
  // and update the cache
  const fetchPromise = (getJson(url) as Promise<T>).then((item: T) => {
    const cacheItem = item as {};

    if (itemCache.isCacheItem(cacheItem)) {
      itemCache.setCacheItem(cacheItem);
    }

    return item;
  });

  return cachePromise ? cachePromise : fetchPromise;
}

export function getItems<T>(url: string | URLX) {
  return getJson(url).then((items: any[]) => {
    if (items) {
      items.forEach(i => {
        if (itemCache.isCacheItem(i)) {
          itemCache.setCacheItem(i);
        }
      });
    }

    return items as T[];
  });
}

export function getJson(url: string | URLX, headers?: { [name: string]: string }) {
  return load(url, 'get', headers).then(r => r.json());
}

export function post(url: string | URLX, body?: any, headers?: { [name: string]: string }) {
  return load(url, 'post', body, headers);
}

export function postJson(url: string | URLX, body?: any, headers?: { [name: string]: string }) {
  return load(url, 'post', body, headers).then(r => r.json());
}

type HttpMethod = 'get' | 'post';

function load(url: string | URLX, method: HttpMethod, body?: any, headers: { [name: string]: string } = {}) {
  // We append the extra querystring here because we can't request the same url for Ajax as what's in
  // the browser address bar. If we use the same url, the browser will use the JSON response from the browser
  // cache to serve up to the user if the user goes back from the site and then forward to it.
  url = new URLX(url);

  let promise = fetch(url.hiddenHref, {
    headers: Object.assign(
      {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        'X-Client-Version': CURRENT_VERSION,
      },
      headers,
    ),
    credentials: 'same-origin',
    method,
    body: body !== undefined ? JSON.stringify(body) : isIE() ? null : undefined, // We use `null` instead of `undefined` here because otherwise IE will send `"undefined"` as body
  })
    .then<any>(r => {
      if (serverHasDifferentMajorVersion(r)) {
        console.info('Nuking the local caches because the server has a higher major version');
        return Promise.all([
          clearCacheAndUnregisterServiceWorker(),
          itemCache.nukeCache(),
          appShell.nukeAppShell(),
        ]).then(() => {
          // Might seem drastic to do a full page reload when we get an Ajax response since it
          // will probably interrupt the user. But a major version change means that data structures
          // differ in a way that the client has to update. Major version changes should be few and far
          // between enough to not make this a usability issue.
          window.location.reload();
          // Return never resolving promise to pause the chain so the page can reload
          return new Promise(() => null);
        });
      } else {
        return r;
      }
    })
    .then(r => {
      if (r.status >= 200 && r.status < 400) {
        return r;
      } else {
        return Promise.reject({
          status: r.status,
        } as LoadFailure);
      }
    });

  if (process.env.NODE_ENV !== 'production') {
    const delayNumber =
      (window.chaosMonkeyMode && window.chaosMonkeyMode.delay) || Math.round(Math.random() * 2 * 100) / 100;
    const failNumber = (window.chaosMonkeyMode && window.chaosMonkeyMode.fail) || 0.5;

    const delay = (value: any) => {
      console.debug('Delaying request for', delayNumber, 'seconds', (url as URLX).hiddenHref);
      return new Promise<any>(resolve => setTimeout(() => resolve(value), delayNumber * 1000));
    };

    const randomlyFail = (value: any) => {
      if (Math.random() < failNumber) {
        console.debug('Simulating failed request, probability: ', failNumber, (url as URLX).hiddenHref);
        return Promise.reject({
          status: 500,
        } as LoadFailure);
      } else {
        return value;
      }
    };

    if (window.chaosMonkeyMode) {
      promise = promise.then(delay).then(randomlyFail);
    }
  }

  return promise;
}

function serverHasDifferentMajorVersion(response: Response) {
  // If the response is an error we might have failed to add the server version
  if (!response.headers.has(VERSION_HEADER_NAME)) {
    console.warn("Response didn't contain a server version in header");
    return false;
  }

  const serverVersion = response.headers.get(VERSION_HEADER_NAME);
  return serverVersion ? majorVersionDiffers(serverVersion, CURRENT_VERSION) : false;
}

export function majorVersionDiffers(version1: string, version2: string) {
  const version1Major = (version1 || '').split('.')[0];
  const version2Major = (version2 || '').split('.')[0];
  return version1Major !== version2Major;
}
